'use client';

import React, { Children, CSSProperties, isValidElement, ReactNode, useEffect, useState } from 'react';
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { pageImageLoader, PageBlock, PageColumn, PageContentV2, PageSection, toPageContent } from '../lib/pageBlocks';
import { useTranslation } from '../lib/i18n';
import useSWR from 'swr';
import { swrFetcher } from '../lib/api';
import BookingWidget from './BookingWidget';
import styles from './PageRenderer.module.css';

function safeHref(value?: string) {
  if (!value) return undefined;
  try {
    const url = new URL(value, typeof window === 'undefined' ? 'https://localhost' : window.location.origin);
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol) ? value : undefined;
  } catch {
    return undefined;
  }
}

function mediaUrl(value?: string, apiBase?: string) {
  const base = apiBase ?? '';
  return value?.startsWith('/api/') ? `${base}${value}` : value;
}

/** Prefix same-site absolute links ("/sportovnimasaz") with the site base
 *  path — needed when a site is previewed under a host it is not mapped to
 *  (dev /jivamasaz/...). On the real CMS host linkBase is ''.
 *  Links starting with "~/" escape the prefix and stay app-relative —
 *  used in help articles to link app routes like /crm/concept. */
function linkHref(value: string | undefined, linkBase?: string) {
  if (!value) return value;
  if (value.startsWith('~/')) return value.slice(1);
  if (linkBase && value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/api/')) {
    return `${linkBase}${value === '/' ? '' : value}` || '/';
  }
  return value;
}

function youtubeEmbedUrl(url?: string) {
  if (!url) return undefined;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (match) return `https://www.youtube.com/embed/${match[1]}`;
  return url.includes('/embed/') ? url : undefined;
}

type InlineEditProps = { value: string; multiline?: boolean; onCommit: (v: string) => void; onCancel: () => void };
function InlineEdit({ value, multiline, onCommit, onCancel }: InlineEditProps) {
  const [draft, setDraft] = useState(value);
  const onBlur = () => onCommit(draft);
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { onCancel(); }
    if (!multiline && e.key === 'Enter') (e.currentTarget as HTMLElement).blur();
    if (multiline && e.ctrlKey && e.key === 'Enter') (e.currentTarget as HTMLElement).blur();
  };
  if (multiline) return <textarea autoFocus value={draft} onChange={e => setDraft(e.target.value)} onBlur={onBlur} onKeyDown={onKey} className={styles.inlineArea} rows={4} />;
  return <input autoFocus value={draft} onChange={e => setDraft(e.target.value)} onBlur={onBlur} onKeyDown={onKey} className={styles.inlineInput} />;
}

type Ctx = {
  preview?: boolean;
  selectedId?: string;
  onSelectBlock?: (id: string) => void;
  onInlineEdit?: (id: string, patch: Record<string, any>) => void;
  /** editor-only: drag a block onto another block to reorder/move */
  onMoveBlock?: (activeId: string, overId: string) => void;
  apiBase?: string;
  linkBase?: string;
  /** bookSlug → existující rezervační služba (mrtvá tlačítka se skryjí) */
  validServiceSlugs?: Set<string>;
};

/** Slugify heading text for stable anchor ids (diacritics stripped, keeps non-latin letters e.g. cyrillic). */
export function headingSlugFromText(text: string): string {
  const norm = text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  let out = '';
  for (const ch of norm) {
    // písmeno = má odlišný lower/uppercase; číslice zachovat; zbytek → pomlčka
    const wordChar = (ch >= '0' && ch <= '9') || ch.toLowerCase() !== ch.toUpperCase();
    out += wordChar ? ch : out && !out.endsWith('-') ? '-' : '';
  }
  return out.replace(/^-+|-+$/g, '');
}

/** Extract plain text from React children (recursively descends into elements). */
function reactNodeText(node: ReactNode): string {
  return Children.toArray(node)
    .map((c) => {
      if (typeof c === 'string' || typeof c === 'number') return String(c);
      if (isValidElement<{ children?: ReactNode }>(c)) return reactNodeText(c.props.children);
      return '';
    })
    .join('');
}

function headingSlug(children: ReactNode): string {
  return headingSlugFromText(reactNodeText(children));
}

export type TocHeading = { level: 2 | 3; text: string; id: string };

/** Extract h2/h3 headings from MARKDOWN/TEXT blocks for a table of contents (same ids as rendered headings). */
export function extractTocHeadings(content: unknown): TocHeading[] {
  const out: TocHeading[] = [];
  const sections = (content as { sections?: unknown })?.sections;
  if (!Array.isArray(sections)) return out;
  for (const section of sections) {
    const columns = (section as { columns?: unknown[] })?.columns;
    if (!Array.isArray(columns)) continue;
    for (const column of columns) {
      const blocks = (column as { blocks?: unknown[] })?.blocks;
      if (!Array.isArray(blocks)) continue;
      for (const block of blocks) {
        const b = block as { enabled?: boolean; type?: string; props?: { content?: unknown } };
        if (b?.enabled === false || (b.type !== 'MARKDOWN' && b.type !== 'TEXT')) continue;
        const text = b.props?.content;
        if (typeof text !== 'string') continue;
        let inFence = false;
        for (const line of text.split('\n')) {
          const trimmed = line.trim();
          if (trimmed.startsWith('```')) { inFence = !inFence; continue; }
          if (inFence) continue;
          const m = /^(#{2,3})\s+(.+)$/.exec(trimmed);
          if (!m) continue;
          const label = m[2]
            .replace(/\s*#+\s*$/, '')
            .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
            .replace(/[*_~`]/g, '')
            .trim();
          if (!label) continue;
          out.push({ level: m[1].length as 2 | 3, text: label, id: headingSlugFromText(label) });
        }
      }
    }
  }
  return out;
}

/** Markdown renderer that rewrites internal links through linkBase. */
function Md({ text, ctx }: { text: string; ctx: Ctx }) {
  const withId = (Tag: 'h2' | 'h3' | 'h4') => {
    const Heading = ({ children }: { children?: ReactNode }) => <Tag id={headingSlug(children)}>{children}</Tag>;
    Heading.displayName = `Md${Tag.toUpperCase()}`;
    return Heading;
  };
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      components={{
        a: ({ href, children }) => <a href={safeHref(linkHref(href, ctx.linkBase))} target={href?.startsWith('http') ? '_blank' : undefined} rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}>{children}</a>,
        h2: withId('h2'), h3: withId('h3'), h4: withId('h4'),
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

type SliderSlide = { title?: string; content?: string; imageUrl?: string; linkUrl?: string; linkLabel?: string };

function HeroSlider({ slides, height, ctx }: { slides: SliderSlide[]; height?: number; ctx: Ctx }) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const list = slides.filter(s => s?.title || s?.content || s?.imageUrl);
  const count = list.length;

  useEffect(() => {
    if (count < 2) return;
    const timer = setInterval(() => setIndex(i => (i + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [count]);

  if (!count) return <div className={styles.placeholder}>{t('cmsEdEmptySlider')}</div>;
  const active = ((index % count) + count) % count;
  const slide = list[active];
  const bg = mediaUrl(slide.imageUrl, ctx.apiBase);

  return (
    <div className={`${styles.slider} ${bg ? styles.heroHasImage : ''}`} style={height ? { minHeight: height } : undefined}>
      {bg && <Image src={bg} alt={slide.title || ''} fill className={styles.heroImage} loader={pageImageLoader} unoptimized sizes="100vw" />}
      {bg && <div className={styles.heroOverlay} />}
      <div className={styles.sliderContent} key={active}>
        {slide.title && <h1>{slide.title}</h1>}
        {slide.content && <Md text={slide.content} ctx={ctx} />}
        {safeHref(linkHref(slide.linkUrl, ctx.linkBase)) && <a className={styles.button} href={safeHref(linkHref(slide.linkUrl, ctx.linkBase))}>{slide.linkLabel || t('cmsEdMore')}</a>}
      </div>
      {count > 1 && (
        <>
          <button type="button" className={`${styles.sliderArrow} ${styles.sliderPrev}`} aria-label={t('cmsEdPrevSlide')} onClick={e => { e.stopPropagation(); setIndex(active - 1); }}>‹</button>
          <button type="button" className={`${styles.sliderArrow} ${styles.sliderNext}`} aria-label={t('cmsEdNextSlide')} onClick={e => { e.stopPropagation(); setIndex(active + 1); }}>›</button>
          <div className={styles.sliderDots}>
            {list.map((_, i) => <button key={i} type="button" aria-label={`${i + 1}`} className={`${styles.sliderDot} ${i === active ? styles.sliderDotActive : ''}`} onClick={e => { e.stopPropagation(); setIndex(i); }} />)}
          </div>
        </>
      )}
    </div>
  );
}

type SortableBlockCtx = {
  setNodeRef: (el: HTMLElement | null) => void;
  transform: ReturnType<typeof useSortable>['transform'];
  transition: string | undefined;
  isDragging: boolean;
  handleProps: Record<string, unknown>;
};

function BlockView({ block, ctx, sortable }: { block: PageBlock; ctx: Ctx; sortable?: SortableBlockCtx }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<{ key: string; value: string } | null>(null);
  const p = block.props || {};
  const st = block.style || {};
  const selected = ctx.selectedId === block.id;

  const editable = (key: string, value: string, children: React.ReactNode) => {
    if (!ctx.preview || !ctx.onInlineEdit) return children;
    if (editing?.key === key) {
      return <InlineEdit value={editing.value} multiline={key === 'content' || key === 'html'} onCommit={v => { setEditing(null); ctx.onInlineEdit!(block.id, { [key]: v }); }} onCancel={() => setEditing(null)} />;
    }
    return <span onDoubleClick={e => { e.stopPropagation(); setEditing({ key, value }); }} className={styles.editable}>{children}</span>;
  };

  const onClick = (e: React.MouseEvent) => {
    if (!ctx.onSelectBlock) return;
    const t = e.target as HTMLElement;
    if (t.closest('a') || t.closest('button') || t.isContentEditable) return;
    e.stopPropagation();
    ctx.onSelectBlock(block.id);
  };

  const alignClass = st.alignment === 'CENTER' ? styles.alignCenter : st.alignment === 'RIGHT' ? styles.alignRight : '';
  const style: CSSProperties = {
    ...(st.width && st.width < 100 ? { flex: `0 0 calc(${st.width}% - 1rem)` } : { flex: '0 0 100%' }),
    ...(st.backgroundColor ? { ['--block-bg' as string]: st.backgroundColor } : {}),
    ...(st.textColor ? { ['--block-color' as string]: st.textColor } : {}),
  };

  let inner: React.ReactNode = null;
  switch (block.type) {
    case 'HEADING': {
      const level = ['h1', 'h2', 'h3', 'h4'].includes(p.level) ? p.level : 'h2';
      const Tag = level as 'h1' | 'h2' | 'h3' | 'h4';
      inner = <Tag className={`${styles.heading} ${p.align === 'center' ? styles.center : p.align === 'right' ? styles.right : ''}`}>{editable('text', p.text ?? '', p.text ?? '')}</Tag>;
      break;
    }
    case 'SLIDER':
      inner = <HeroSlider slides={p.slides ?? []} height={p.height} ctx={ctx} />;
      break;
    case 'TEXT':
    case 'MARKDOWN': {
      // Celý blok jako odkaz (jen mimo editor — v editoru klik vybírá blok); šipka na konci textu
      const textHref = !ctx.onSelectBlock ? safeHref(linkHref(p.linkUrl, ctx.linkBase)) : null;
      const textEl = <div className={styles.text}>
        {p.title && <h2 className={styles.sectionTitle}>{editable('title', p.title, p.title)}</h2>}
        {p.content ? editable('content', p.content, <Md text={p.content} ctx={ctx} />) : <span className={styles.placeholder}>{t('cmsEdEmptyText')}</span>}
      </div>;
      inner = textHref ? <a href={textHref} className={styles.blockLink}>{textEl}</a> : textEl;
      break;
    }
    case 'HERO':
      inner = <div className={`${styles.hero} ${p.imageUrl ? styles.heroHasImage : ''}`} style={p.height ? { minHeight: p.height } : undefined}>
        {p.imageUrl && <Image src={mediaUrl(p.imageUrl, ctx.apiBase) || ''} alt={p.imageAlt || ''} fill className={styles.heroImage} loader={pageImageLoader} unoptimized sizes="100vw" />}
        {p.imageUrl && <div className={styles.heroOverlay} />}
        <div className={`${styles.heroContent} ${p.align === 'left' ? styles.left : p.align === 'right' ? styles.right : ''}`}>
          {p.title && <h1>{editable('title', p.title, p.title)}</h1>}
          {p.content && editable('content', p.content, <Md text={p.content} ctx={ctx} />)}
          {safeHref(linkHref(p.linkUrl, ctx.linkBase)) && <a className={styles.button} href={safeHref(linkHref(p.linkUrl, ctx.linkBase))}>{p.linkLabel || t('cmsEdMore')}</a>}
        </div>
      </div>;
      break;
    case 'IMAGE': {
      const rotateSrcs = [p.imageUrl, ...(typeof p.rotateUrls === 'string' ? p.rotateUrls.split(',') : [])]
        .map((u: string) => (u ?? '').trim())
        .filter(Boolean)
        .map((u: string) => mediaUrl(u, ctx.apiBase) || '');
      const img = !p.imageUrl
        ? <div className={styles.placeholder}>{t('cmsEdChooseImage')}</div>
        : p.rotate && p.rotate !== 'none' && rotateSrcs.length > 1
          ? <RotatingImage srcs={rotateSrcs} alt={p.imageAlt || ''} interval={Number(p.rotateInterval) || 5} mode={p.rotate} className={styles.image} />
          : <Image src={mediaUrl(p.imageUrl, ctx.apiBase) || ''} alt={p.imageAlt || ''} width={1200} height={800} className={styles.image} loader={pageImageLoader} unoptimized />;
      const wrapped = safeHref(linkHref(p.linkUrl, ctx.linkBase)) ? <a href={safeHref(linkHref(p.linkUrl, ctx.linkBase))} target="_blank" rel="noopener noreferrer">{img}</a> : img;
      const figure = <figure className={styles.figure}>{wrapped}{p.caption && <figcaption>{p.caption}</figcaption>}</figure>;
      inner = p.sticky ? <div style={{ position: 'sticky', top: '90px' }}>{figure}</div> : figure;
      break;
    }
    case 'GALLERY':
      inner = <div>
        {p.title && <h2 className={styles.sectionTitle}>{p.title}</h2>}
        <div className={styles.gallery}>
          {(p.images ?? []).filter((i: any) => i?.url).map((img: any, i: number) => {
            const el = <Image key={i} src={mediaUrl(img.url, ctx.apiBase) || ''} alt={img.alt || ''} width={600} height={400} className={styles.galleryImage} loader={pageImageLoader} unoptimized />;
            return safeHref(linkHref(img.link, ctx.linkBase)) ? <a key={i} href={safeHref(linkHref(img.link, ctx.linkBase))}>{el}</a> : el;
          })}
          {!(p.images ?? []).some((i: any) => i?.url) && <div className={styles.placeholder}>{t('cmsEdEmptyGallery')}</div>}
        </div>
      </div>;
      break;
    case 'VIDEO': {
      const embed = youtubeEmbedUrl(p.url);
      inner = <div className={styles.video}>
        {p.title && <h2 className={styles.sectionTitle}>{p.title}</h2>}
        {embed ? <iframe src={embed} title={p.title || 'video'} allowFullScreen /> : <div className={styles.placeholder}>{t('cmsEdVideoUrl')}</div>}
      </div>;
      break;
    }
    case 'BUTTON':
      inner = <div className={`${styles.buttonRow} ${p.align === 'left' ? styles.left : p.align === 'right' ? styles.right : ''}`}>
        {safeHref(linkHref(p.url, ctx.linkBase)) ? <a className={styles.button} href={safeHref(linkHref(p.url, ctx.linkBase))}>{editable('label', p.label ?? t('cmsEdButton'), p.label || t('cmsEdButton'))}</a> : <span className={styles.button}>{editable('label', p.label ?? t('cmsEdButton'), p.label || t('cmsEdButton'))}</span>}
      </div>;
      break;
    case 'CTA':
      inner = <div className={styles.cta}>
        {p.title && <h2>{editable('title', p.title, p.title)}</h2>}
        {p.content && editable('content', p.content, <Md text={p.content} ctx={ctx} />)}
        {safeHref(linkHref(p.linkUrl, ctx.linkBase)) && <a className={styles.button} href={safeHref(linkHref(p.linkUrl, ctx.linkBase))}>{p.linkLabel || t('cmsEdMore')}</a>}
      </div>;
      break;
    case 'LINKS': {
      const svg = (d: ReactNode) => (
        <svg className={styles.linkIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
      );
      const iconFor = (url?: string): ReactNode => {
        const u = url ?? '';
        if (u.includes('whatsapp')) return svg(<path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />);
        if (u.includes('t.me') || u.includes('telegram')) return svg(<><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></>);
        if (u.includes('facebook')) return svg(<path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />);
        if (u.includes('instagram')) return svg(<><rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="4" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></>);
        if (u.includes('mapy.cz') || u.includes('maps') || u.includes('goo.gl')) return svg(<><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></>);
        if (u.startsWith('tel:')) return svg(<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />);
        if (u.startsWith('mailto:')) return svg(<><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 5L2 7" /></>);
        if (!u.startsWith('http') && !u.startsWith('tel:') && !u.startsWith('mailto:')) return null;
        return svg(<><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>);
      };
      inner = <div>
        {p.title && <h2 className={styles.sectionTitle}>{p.title}</h2>}
        <ul className={p.inline ? styles.linkListInline : styles.linkList}>
          {(p.links ?? []).filter((l: any) => l?.label || l?.url).map((l: any, i: number) => <li key={i}>{safeHref(linkHref(l.url, ctx.linkBase)) ? <a href={safeHref(linkHref(l.url, ctx.linkBase))} target={l.url?.startsWith('http') ? '_blank' : undefined} rel={l.url?.startsWith('http') ? 'noopener noreferrer' : undefined}>{iconFor(l.url)}{l.label || l.url}</a> : <span>{l.label}</span>}</li>)}
        </ul>
      </div>;
      break;
    }
    case 'FAQ':
      inner = <div>
        {p.title && <h2 className={styles.sectionTitle}>{p.title}</h2>}
        {(p.items ?? []).map((item: any, i: number) => (
          <details key={i} className={styles.faq}><summary>{item.question || t('cmsEdQuestion')}</summary><div className={styles.faqAnswer}><Md text={item.answer || ''} ctx={ctx} /></div></details>
        ))}
      </div>;
      break;
    case 'PRICELIST':
      inner = <div>
        {p.title && <h2 className={styles.sectionTitle}>{p.title}</h2>}
        {p.layout === 'table' ? (
          <div className={styles.priceList}>
            {(p.items ?? []).map((item: any, i: number) => (
              <div key={i} className={styles.priceRow}>
                <div className={styles.priceInfo}>
                  <div className={styles.priceRowName}>
                    {item.linkUrl ? <a href={item.linkUrl}>{item.name}</a> : item.name}
                  </div>
                  {item.description && <div className={styles.priceRowDesc}>{item.description}</div>}
                </div>
                <span className={styles.priceLeader} aria-hidden="true" />
                <div className={styles.priceActions}>
                  {item.bookSlug && (!ctx.validServiceSlugs || ctx.validServiceSlugs.has(item.bookSlug)) && (
                    <a className={styles.button} href="#rezervace"
                       onClick={(e) => { e.preventDefault();
                         window.dispatchEvent(new CustomEvent('cms-book-service', { detail: item.bookSlug }));
                         const el = document.getElementById('rezervace');
                         el?.setAttribute('tabindex', '-1');
                         el?.scrollIntoView({ behavior: 'smooth' });
                         el?.focus({ preventScroll: true }); }}>
                      {t('bookReserve')}
                    </a>
                  )}
                  <div className={styles.priceValue}>{item.price}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.priceGrid}>
            {(p.items ?? []).map((item: any, i: number) => (
              <div key={i} className={`${styles.priceCard} ${item.featured ? styles.featured : ''}`}>
                <div className={styles.priceName}>{item.name}</div>
                <div className={styles.priceValue}>{item.price}</div>
                {item.description && <div className={styles.priceDesc}>{item.description}</div>}
              </div>
            ))}
          </div>
        )}
      </div>;
      break;
    case 'TESTIMONIALS':
      inner = <div>
        {p.title && <h2 className={styles.sectionTitle}>{p.title}</h2>}
        <div className={styles.testimonialGrid}>
          {(p.items ?? []).map((item: any, i: number) => (
            <blockquote key={i} className={styles.testimonial}>
              <p>„{item.quote}“</p>
              <footer>{item.author}{item.role ? ` · ${item.role}` : ''}</footer>
            </blockquote>
          ))}
        </div>
      </div>;
      break;
    case 'MAP':
      inner = <div>
        {p.title && <h2 className={styles.sectionTitle}>{p.title}</h2>}
        {p.embedUrl ? <iframe src={p.embedUrl} className={styles.map} loading="lazy" /> : <div className={styles.placeholder}>{t('cmsEdMapUrl')}</div>}
        {p.address && <p className={styles.address}>{p.address}</p>}
      </div>;
      break;
    case 'FOOTER': {
      const year = String(new Date().getFullYear());
      inner = (
        <div className={styles.siteFooter}>
          <div className={styles.siteFooterGrid}>
            <div>
              {p.brand && <div className={styles.siteFooterBrand}>{p.brand}</div>}
              {p.tagline && <div className={styles.siteFooterTagline}>{p.tagline}</div>}
            </div>
            {(p.links ?? []).length > 0 && (
              <ul className={styles.siteFooterLinks}>
                {(p.links ?? []).filter((l: any) => l?.label || l?.url).map((l: any, i: number) => (
                  <li key={i}><a href={safeHref(linkHref(l.url, ctx.linkBase))}>{l.label || l.url}</a></li>
                ))}
              </ul>
            )}
            {p.contact && <div className={styles.siteFooterContact}><Md text={p.contact} ctx={ctx} /></div>}
          </div>
          {p.copyright && (
            <div className={styles.siteFooterCopy}>{String(p.copyright).replace('{year}', year)}</div>
          )}
        </div>
      );
      break;
    }
    case 'DIVIDER':
      inner = p.style === 'ornament' ? <div className={styles.ornament}>❦</div> : p.style === 'space' ? <div style={{ height: 24 }} /> : <hr className={styles.hr} />;
      break;
    case 'SPACER':
      inner = <div style={{ height: p.height ?? 48 }} />;
      break;
    case 'BOOKING':
      inner = <div>
        {p.title && <h2 className={styles.sectionTitle}>{p.title}</h2>}
        {p.content && <div className={styles.bookingIntro}><Md text={p.content} ctx={ctx} /></div>}
        {p.serviceSlug ? <BookingWidget slugs={String(p.serviceSlug).split(',').map((s: string) => s.trim()).filter((s: string) => Boolean(s) && (!ctx.validServiceSlugs || ctx.validServiceSlugs.has(s)))} compact={!!p.compactServices} /> : <div className={styles.placeholder}>{t('cmsEdChooseBooking')}</div>}
      </div>;
      break;
    case 'EMBED':
      inner = <div className={styles.embed} dangerouslySetInnerHTML={{ __html: p.html ?? '' }} />;
      break;
    case 'ESHOP':
      inner = <div>
        {p.title && <h2 className={styles.sectionTitle}>{p.title}</h2>}
        {p.content && <Md text={p.content} ctx={ctx} />}
        <EshopCatalogBlock
          categoryId={p.categoryId ? Number(p.categoryId) : undefined}
          limit={typeof p.limit === 'number' ? p.limit : 8}
          featuredOnly={!!p.featuredOnly}
          apiBase={ctx.apiBase}
        />
      </div>;
      break;
    default:
      inner = <div className={styles.placeholder}>{t('cmsEdUnknownBlock')}: {block.type}</div>;
  }

  return (
    <section
      ref={sortable?.setNodeRef}
      onClick={onClick}
      data-block-type={block.type}
      data-block-id={block.id}
      className={`${styles.block} ${alignClass} ${ctx.preview ? styles.previewBlock : ''} ${selected ? styles.selected : ''} ${sortable?.isDragging ? styles.dragging : ''}`}
      style={{ ...style, ...(sortable ? { transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition } : {}) }}
    >
      {sortable && <button type="button" {...sortable.handleProps} className={styles.dragHandle} aria-label={t('cmsEdDragBlock')}>⋮⋮</button>}
      {inner}
    </section>
  );
}

function DraggableBlock({ block, ctx }: { block: PageBlock; ctx: Ctx }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  return <BlockView block={block} ctx={ctx} sortable={{ setNodeRef, transform, transition, isDragging, handleProps: { ...attributes, ...listeners } }} />;
}

function ColumnView({ column, ctx }: { column: PageColumn; ctx: Ctx }) {
  const width = column.width && column.width < 100 ? `calc(${column.width}% - 1rem)` : '100%';
  const items = column.blocks.filter(b => b.enabled !== false);
  const list = items.map(block => ctx.onMoveBlock ? <DraggableBlock key={block.id} block={block} ctx={ctx} /> : <BlockView key={block.id} block={block} ctx={ctx} />);
  return (
    <div className={styles.column} style={{ flex: `0 0 ${width}` }}>
      {ctx.onMoveBlock ? <SortableContext items={items.map(b => b.id)} strategy={verticalListSortingStrategy}>{list}</SortableContext> : list}
    </div>
  );
}

function SectionView({ section, ctx }: { section: PageSection; ctx: Ctx }) {
  const s = section.settings || {};
  const style: CSSProperties = {
    ...(s.backgroundColor ? { backgroundColor: s.backgroundColor } : {}),
    ...(s.textColor ? { color: s.textColor } : {}),
    ...(s.paddingY != null ? { paddingTop: s.paddingY, paddingBottom: s.paddingY } : {}),
    ...(s.paddingTop != null ? { paddingTop: s.paddingTop } : {}),
    ...(s.paddingBottom != null ? { paddingBottom: s.paddingBottom } : {}),
  };
  return (
    <div id={s.anchor || undefined} className={styles.section} style={style}
      data-effect={s.effect || 'up'}
      data-stagger={s.stagger ? '' : undefined}>
      <div className={`${styles.sectionInner} ${s.container === 'full' ? styles.full : ''}`}>
        {section.columns.map(col => <ColumnView key={col.id} column={col} ctx={ctx} />)}
      </div>
    </div>
  );
}

/** Rotating image — 'fade' crossfades on interval, 'random' picks a random frame after mount. */
function RotatingImage({ srcs, alt, interval, mode, className }: {
  srcs: string[]; alt: string; interval: number; mode: string; className?: string;
}) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (mode === 'random') {
      setIdx(Math.floor(Math.random() * srcs.length));
      return;
    }
    const t = setInterval(() => setIdx(i => (i + 1) % srcs.length), Math.max(1, interval) * 1000);
    return () => clearInterval(t);
  }, [srcs.length, interval, mode]);
  return (
    <span style={{ position: 'relative', display: 'block' }}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={srcs[0]} alt={alt} className={className} style={{ visibility: 'hidden' }} />
      {srcs.map((s, i) => (
        // eslint-disable-next-line @next/next/no-img-element
        <img key={s + i} src={s} alt={i === idx ? alt : ''} aria-hidden={i !== idx}
          className={className}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', transition: 'opacity 0.8s ease-in-out',
            opacity: i === idx ? 1 : 0,
          }} />
      ))}
    </span>
  );
}

/** Embedded eshop catalog grid for CMS pages — fetches public eshop API. */
function EshopCatalogBlock({ categoryId, limit, featuredOnly, apiBase }: {
  categoryId?: number;
  limit: number;
  featuredOnly: boolean;
  apiBase?: string;
}) {
  type ShopProduct = {
    id: number; name: string; slug: string;
    price: number | null; groupPrice: number | null; currency: string;
    imageAssetId: number | null;
  };
  const [products, setProducts] = useState<ShopProduct[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const base = apiBase ?? '';
    const url = featuredOnly
      ? `${base}/api/public/eshop/products/featured`
      : `${base}/api/public/eshop/products?size=${limit}${categoryId ? `&categoryId=${categoryId}` : ''}`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => setProducts(Array.isArray(d) ? d.slice(0, limit) : (d.content || []).slice(0, limit)))
      .catch(() => setFailed(true));
  }, [categoryId, limit, featuredOnly, apiBase]);

  if (failed) return null;
  if (!products) return <div style={{ minHeight: 80 }} />;
  if (products.length === 0) return null;

  const price = (p: ShopProduct) => {
    const v = p.groupPrice ?? p.price;
    return v == null ? null : `${v.toLocaleString('cs-CZ', { minimumFractionDigits: 2 })} ${p.currency || 'CZK'}`;
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem' }}>
      {products.map((p) => (
        <a
          key={p.id}
          href={`/shop/${p.slug}`}
          style={{ display: 'block', border: '1px solid var(--surface, #e5e0d5)', borderRadius: 8, overflow: 'hidden', background: 'var(--surface, #faf8f3)', color: 'inherit', textDecoration: 'none' }}
        >
          <div style={{ aspectRatio: '1', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {p.imageAssetId != null && (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={`${apiBase ?? ''}/api/public/eshop/assets/${p.imageAssetId}`} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
          </div>
          <div style={{ padding: '0.6rem 0.8rem' }}>
            <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>{p.name}</div>
            {price(p) && <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--accent, #5a7247)', marginTop: 2 }}>{price(p)}</div>}
          </div>
        </a>
      ))}
    </div>
  );
}

export type PageRendererProps = {
  content: unknown; // PageContentV2 or legacy LegacyPageContent
  siteSlug?: string;
  apiBase?: string;
  linkBase?: string;
  preview?: boolean;
  selectedId?: string;
  onSelectBlock?: (id: string) => void;
  onInlineEdit?: (id: string, patch: Record<string, any>) => void;
  onMoveBlock?: (activeId: string, overId: string) => void;
};

export default function PageRenderer({ content, siteSlug, apiBase, linkBase, preview, selectedId, onSelectBlock, onInlineEdit, onMoveBlock }: PageRendererProps) {
  const pathname = usePathname();
  const [showTop, setShowTop] = useState(false);
  const pageRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (preview) return;
    const els = pageRef.current?.querySelectorAll<HTMLElement>(`.${styles.section}`);
    if (!els) return;
    // Doťazové kotvy — plynulé odscrollování (jen mimo editor)
    const root = document.documentElement;
    const prevScrollBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'smooth';
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add(styles.inView); obs.unobserve(e.target); } });
    }, { threshold: 0.08 });
    els.forEach(el => {
      const effect = el.dataset.effect || 'up';
      if (effect === 'none') return;
      if (el.dataset.stagger !== undefined) {
        // Stagger — bloky animují jednotlivě (sekce sama zůstane vidět)
        el.querySelectorAll<HTMLElement>(`.${styles.block}`).forEach((b, i) => {
          b.classList.add(styles.fadeSection);
          b.setAttribute('data-effect', effect);
          b.style.transitionDelay = `${Math.min(i, 10) * 80}ms`;
          obs.observe(b);
        });
        return;
      }
      el.classList.add(styles.fadeSection);
      obs.observe(el);
    });
    const onScroll = () => setShowTop(window.scrollY > 500);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { obs.disconnect(); window.removeEventListener('scroll', onScroll); root.style.scrollBehavior = prevScrollBehavior; };
  }, [content, preview]);
  const page: PageContentV2 = toPageContent(content);
  const theme = page.theme || {};
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const canDrag = !!preview && !!onMoveBlock;
  const onDragEnd = (e: DragEndEvent) => {
    if (e.over && e.active.id !== e.over.id) onMoveBlock?.(String(e.active.id), String(e.over.id));
  };
  const vars = {
    ...(theme.backgroundColor ? { '--bg': theme.backgroundColor } : {}),
    ...(theme.surfaceColor ? { '--surface': theme.surfaceColor } : {}),
    ...(theme.textColor ? { '--text': theme.textColor } : {}),
    ...(theme.accentColor ? { '--accent': theme.accentColor } : {}),
    ...(theme.fontFamily ? { '--font': theme.fontFamily } : {}),
  } as CSSProperties;
  // Vazba ceník→rezervace: seber bookSlug/serviceSlug z celé stránky a ověř bulkem,
  // že služby existují — mrtvé Rezervovat odkazy se skryjí.
  const neededSlugs = (() => {
    const set = new Set<string>();
    for (const s of page.sections) for (const c of s.columns) for (const b of c.blocks) {
      const props = (b as any).props ?? {};
      for (const it of props.items ?? []) if (it.bookSlug) set.add(String(it.bookSlug));
      if (props.serviceSlug) for (const slug of String(props.serviceSlug).split(',')) if (slug.trim()) set.add(slug.trim());
    }
    return Array.from(set);
  })();
  const { data: bookableServices } = useSWR<{ publicSlug: string }[]>(
    neededSlugs.length && !preview ? ['cms-bookable', ...neededSlugs] : null,
    async () => swrFetcher<{ publicSlug: string }[]>(
      `/api/public/reservations/services?slugs=${neededSlugs.map(encodeURIComponent).join(',')}`),
    { revalidateOnFocus: false },
  );
  const validServiceSlugs = bookableServices ? new Set(bookableServices.map(s => s.publicSlug)) : undefined;
  const ctx: Ctx = { preview, selectedId, onSelectBlock, onInlineEdit, onMoveBlock: canDrag ? onMoveBlock : undefined, apiBase, linkBase, validServiceSlugs };
  const sections = page.sections.map(section => <SectionView key={section.id} section={section} ctx={ctx} />);
  return (
    <div ref={pageRef} className={styles.page} data-site={siteSlug || undefined} style={vars}>
      {canDrag ? <DndContext sensors={sensors} onDragEnd={onDragEnd}>{sections}</DndContext> : sections}
      {!preview && showTop && (
        <button type="button" className={styles.scrollTop} aria-label="Nahoru"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>↑</button>
      )}
    </div>
  );
}
